import React, { useCallback, useState, useEffect, useRef } from "react";
import { Modal, TextField } from "../../../components";
import { CheckCircleIcon, SearchIcon } from "../../../components/icons";
import { debounce } from "lodash";
import { useSelector } from "react-redux";
import {
  getActiveOrganization,
  getOrganizations,
} from "../../../redux/selectors";
import { OrganizationModel } from "../../../utils/types";
import { switchOrganization } from "../../../redux/actions";
import { useAppDispatch } from "../../../hooks";

/**
 * One selectable account row. The org the user is already on renders through
 * this too -- bold, ticked and `aria-current` -- so there is a single row
 * contract rather than a hand-copied one, and the Cypress specs that pick the
 * already-active account keep resolving to `account-selector-menu-item`.
 * Selecting it is a no-op switch (`switchOrganization` short-circuits on a
 * matching uuid) that just closes the modal.
 */
const OrganizationRow: React.FC<{
  org: OrganizationModel;
  onSelect: (org: OrganizationModel) => void;
  isActive?: boolean;
  isHighlighted?: boolean;
}> = ({ org, onSelect, isActive = false, isHighlighted = false }) => (
  <div
    data-cy="account-selector-menu-item"
    aria-current={isActive || undefined}
    onClick={() => onSelect(org)}
    className={`flex flex-row justify-between p-5 cursor-pointer hover:bg-gray-f4 border-b border-gray-200${
      isActive ? " font-bold" : ""
    }${isHighlighted ? " bg-blue-dark hover:text-black text-white" : ""}`}
  >
    {org.name}
    {isActive && <CheckCircleIcon className="mr-80" color={"blue"} />}
  </div>
);

const AccountSelector: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [search, setSearch] = useState("");
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const activeOrganization = useSelector(getActiveOrganization);
  const organizations = useSelector(getOrganizations);

  useEffect(() => {
    if (textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, []);

  const setNewOrganization = (item: OrganizationModel) => {
    // The thunk raises the global spinner synchronously, so the overlay covers
    // the reload from here on; a failed switch leaves the previous org active
    // and reports itself with a toast.
    dispatch(switchOrganization(item));
    onClose();
  };

  const onSearch = (value) => {
    setSearch(value);
    // The highlight indexes into the results, so a new query invalidates it.
    setActiveIndex(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      // With no results, `Math.min(prev + 1, -1)` would park the highlight on
      // -1 and leave Enter inert. Searching the active org's own name now
      // yields an empty list, so this is reachable (SD-3757).
      if (filteredOrganizations.length === 0) return;
      setActiveIndex((prev) =>
        prev === null
          ? 0
          : Math.min(prev + 1, filteredOrganizations.length - 1),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filteredOrganizations.length === 0) return;
      setActiveIndex((prev) => (prev === null ? 0 : Math.max(prev - 1, 0)));
    } else if (e.key === "Enter") {
      if (activeIndex !== null && filteredOrganizations[activeIndex]) {
        setNewOrganization(filteredOrganizations[activeIndex]);
      }
    }
  };

  // `name` is not guaranteed: Preloader takes the active org straight from
  // `user.default_org`, which UserModel stores raw rather than through
  // OrganizationModel, so it carries no `name: ""` default.
  const matchesSearch = (org: OrganizationModel) =>
    (org?.name || "").toLowerCase().includes(search.toLowerCase());

  // The active org has its own row above the list, so leaving it in the results
  // showed it twice whenever the search matched it (SD-3757). Compared on uuid,
  // not name, so a same-named sibling org still lists.
  const activeUuid = activeOrganization?.uuid;
  const filteredOrganizations = organizations.filter(
    (org) => org.uuid !== activeUuid && matchesSearch(org),
  );

  // A search that only matches the active org's row still found something.
  const activeOrganizationMatches =
    !!activeOrganization && matchesSearch(activeOrganization);

  useEffect(() => {
    if (listRef.current) {
      const activeItem = listRef.current.children[activeIndex as number];
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [activeIndex, filteredOrganizations]);

  return (
    <Modal title={"Switch Account"} size="md" onClose={onClose}>
      <div className="flex flex-col pb-5">
        <div className="py-5 font-medium">
          <p>Please select or search for an account from the following list.</p>
        </div>
        <div className="pb-5">
          <TextField
            fullWidth
            className="!rounded-full !bg-gray-100"
            icon={<SearchIcon size={16} color="primary" />}
            placeholder="Search for an account"
            clearable
            value={search}
            onChange={onSearch}
            onKeyDown={handleKeyDown}
            inputRef={textFieldRef}
          />
        </div>
        <div>
          {activeOrganization ? (
            <OrganizationRow
              org={activeOrganization}
              onSelect={setNewOrganization}
              isActive
            />
          ) : (
            <div className="flex flex-row font-bold justify-between p-5 border-b border-gray-200">
              No active organization
            </div>
          )}
        </div>
        <div
          ref={listRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className="h-65 overflow-auto"
        >
          {filteredOrganizations.map((org, index) => (
            <OrganizationRow
              key={org.uuid}
              org={org}
              onSelect={setNewOrganization}
              isHighlighted={index === activeIndex}
            />
          ))}
          {filteredOrganizations.length === 0 &&
            search &&
            !activeOrganizationMatches && (
              <p className="text-gray-500 text-center py-2">No results found</p>
            )}
        </div>
      </div>
    </Modal>
  );
};

export default AccountSelector;
